import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';
import { Play, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { awsRum } from 'Utils/AwsRum';
import './HistoryOfPianoMusic.css';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';

const TIMELINE_START = 1715;
const TIMELINE_END = 1998;
const TIMELINE_SPAN = TIMELINE_END - TIMELINE_START;
const ROADMAP_WIDTH = 12000;
const ROADMAP_HEIGHT = 900;
const ROADMAP_INSET = 120;
const PATH_MIDLINE = 440;
const PATH_LANE_OFFSETS = [-250, -165, -75, 35, 145, 255];
const MIN_NODE_X_GAP = 118;
const DUPLICATE_YEAR_X_SPACING = 84;
const PATH_CURVE_TENSION = 0.72;

type EraName = 'Baroque' | 'Classical' | 'Romantic' | 'Modern' | 'Jazz';

type Era = {
  name: EraName;
  start: number;
  end: number;
  summary: string;
  color: string;
};

type RepertoireNode = {
  year: number;
  composer: string;
  title: string;
  era: EraName;
  reason: string;
  lane: number;
  spotlight?: boolean;
};

type InstrumentMilestone = {
  year: number;
  title: string;
  impact: string;
};

type SourceLink = {
  label: string;
  href: string;
};

type RoadmapPoint = {
  x: number;
  y: number;
};

type RoadmapDragState = {
  active: boolean;
  pointerId: number | null;
  startX: number;
  scrollLeft: number;
};

const eras: Era[] = [
  {
    name: 'Baroque',
    start: 1715,
    end: 1750,
    summary: 'Counterpoint, dance rhythm, ornament, and the last great harpsichord literature.',
    color: '#D4825F',
  },
  {
    name: 'Classical',
    start: 1750,
    end: 1820,
    summary: 'Phrase clarity, sonata form, singing melody, and the fortepiano taking over.',
    color: '#E1B857',
  },
  {
    name: 'Romantic',
    start: 1820,
    end: 1890,
    summary: 'The piano becomes personal, orchestral, virtuosic, and central to recital culture.',
    color: '#AF7EDE',
  },
  {
    name: 'Modern',
    start: 1888,
    end: 1998,
    summary: 'Color harmony, percussive attack, abstraction, minimalism, and new keyboard sound.',
    color: '#68C7BD',
  },
  {
    name: 'Jazz',
    start: 1899,
    end: 1984,
    summary: 'Ragtime, stride, swing, bebop, modal voicings, and electric keyboard language.',
    color: '#EF7E8E',
  },
];

const repertoirePath: RepertoireNode[] = [
  {
    year: 1717,
    composer: 'Couperin',
    title: 'Les Barricades Mysterieuses',
    era: 'Baroque',
    reason: 'Ornament, style, and clavecin elegance before the piano fully arrives.',
    lane: 1,
  },
  {
    year: 1720,
    composer: 'J. S. Bach',
    title: 'Invention No. 1 in C Major',
    era: 'Baroque',
    reason: 'Two-voice independence and contrapuntal clarity in miniature.',
    lane: 4,
  },
  {
    year: 1722,
    composer: 'J. S. Bach',
    title: 'Prelude in C Major, WTC I',
    era: 'Baroque',
    reason: 'A transparent harmonic path that still teaches hand balance.',
    lane: 2,
  },
  {
    year: 1738,
    composer: 'D. Scarlatti',
    title: 'Sonata in D Minor, K. 141',
    era: 'Baroque',
    reason: 'Repeated notes, hand crossing, and Iberian keyboard brilliance.',
    lane: 0,
  },
  {
    year: 1766,
    composer: 'C. P. E. Bach',
    title: 'Solfeggietto',
    era: 'Classical',
    reason: 'The empfindsamer style turns keyboard passagework into rhetoric.',
    lane: 3,
  },
  {
    year: 1774,
    composer: 'Haydn',
    title: 'Sonata in C Major, Hob. XVI:35',
    era: 'Classical',
    reason: 'Classical phrase design and wit in a clear sonata plan.',
    lane: 5,
  },
  {
    year: 1783,
    composer: 'Mozart',
    title: 'Rondo alla Turca, K. 331',
    era: 'Classical',
    reason: 'Public-facing character, crisp articulation, and comic propulsion.',
    lane: 1,
  },
  {
    year: 1788,
    composer: 'Mozart',
    title: 'Sonata in C Major, K. 545',
    era: 'Classical',
    reason: 'A compact entry point into clean Classical piano style.',
    lane: 4,
  },
  {
    year: 1797,
    composer: 'Clementi',
    title: 'Sonatina Op. 36 No. 1',
    era: 'Classical',
    reason: 'A practical bridge from studies into repertoire-shaped sonata writing.',
    lane: 2,
  },
  {
    year: 1798,
    composer: 'Beethoven',
    title: 'Pathetique Sonata, Op. 13',
    era: 'Classical',
    reason: 'Drama, weight, and expanded dynamic range point toward Romanticism.',
    lane: 0,
  },
  {
    year: 1801,
    composer: 'Beethoven',
    title: 'Moonlight Sonata, Op. 27 No. 2',
    era: 'Classical',
    reason: 'Texture and resonance become the emotional center of the piece.',
    lane: 3,
  },
  {
    year: 1810,
    composer: 'Beethoven',
    title: 'Fur Elise',
    era: 'Classical',
    reason: 'A familiar hook with articulation, pedaling, and character changes.',
    lane: 5,
  },
  {
    year: 1827,
    composer: 'Schubert',
    title: 'Impromptu Op. 90 No. 2',
    era: 'Romantic',
    reason: 'Song-like long lines meet repeated figuration and harmonic drift.',
    lane: 1,
  },
  {
    year: 1830,
    composer: 'Mendelssohn',
    title: 'Song Without Words Op. 19 No. 1',
    era: 'Romantic',
    reason: 'The piano learns to sing without text.',
    lane: 4,
  },
  {
    year: 1831,
    composer: 'Chopin',
    title: 'Nocturne Op. 9 No. 2',
    era: 'Romantic',
    reason: 'Bel canto phrasing, rubato, and ornament become pianistic language.',
    lane: 2,
    spotlight: true,
  },
  {
    year: 1835,
    composer: 'Chopin',
    title: 'Ballade No. 1 in G Minor, Op. 23',
    era: 'Romantic',
    reason: 'Narrative form, harmonic distance, and the piano as a dramatic storyteller.',
    lane: 0,
    spotlight: true,
  },
  {
    year: 1836,
    composer: 'Clara Schumann',
    title: 'Notturno, Op. 6 No. 2',
    era: 'Romantic',
    reason: 'Poetic character writing from a central Romantic recital innovator.',
    lane: 3,
  },
  {
    year: 1838,
    composer: 'Robert Schumann',
    title: 'Traumerei',
    era: 'Romantic',
    reason: 'A short character piece where voicing is everything.',
    lane: 4,
  },
  {
    year: 1839,
    composer: 'Chopin',
    title: 'Prelude Op. 28 No. 4',
    era: 'Romantic',
    reason: 'Economy, harmonic gravity, and expressive control in one page.',
    lane: 2,
    spotlight: true,
  },
  {
    year: 1841,
    composer: 'Fanny Hensel',
    title: 'September: At the River',
    era: 'Romantic',
    reason: 'A lyrical calendar piece with Romantic color and flow.',
    lane: 5,
  },
  {
    year: 1845,
    composer: 'Chopin',
    title: 'Barcarolle, Op. 60',
    era: 'Romantic',
    reason: 'Late Chopin: rocking motion, luminous harmony, and large-scale form.',
    lane: 0,
    spotlight: true,
  },
  {
    year: 1850,
    composer: 'Liszt',
    title: 'Liebestraum No. 3',
    era: 'Romantic',
    reason: 'Concert virtuosity fused with a long vocal line.',
    lane: 3,
  },
  {
    year: 1867,
    composer: 'Grieg',
    title: 'Arietta, Op. 12 No. 1',
    era: 'Romantic',
    reason: 'National color and intimate miniature writing.',
    lane: 1,
  },
  {
    year: 1874,
    composer: 'Mussorgsky',
    title: 'Pictures at an Exhibition: Promenade',
    era: 'Romantic',
    reason: 'The piano becomes architectural, pictorial, and orchestral.',
    lane: 5,
  },
  {
    year: 1888,
    composer: 'Satie',
    title: 'Gymnopedie No. 1',
    era: 'Modern',
    reason: 'A spare anti-virtuoso sound world opens modern color.',
    lane: 2,
  },
  {
    year: 1892,
    composer: 'Rachmaninoff',
    title: 'Prelude in C-sharp Minor',
    era: 'Romantic',
    reason: 'Late Romantic sonority, bells, and dense resonance.',
    lane: 0,
  },
  {
    year: 1892,
    composer: 'Amy Beach',
    title: 'Dreaming, Op. 15 No. 3',
    era: 'Romantic',
    reason: 'American Romantic piano writing with intimate color.',
    lane: 4,
  },
  {
    year: 1893,
    composer: 'Brahms',
    title: 'Intermezzo Op. 118 No. 2',
    era: 'Romantic',
    reason: 'Compressed late style: inner voices, warmth, and structural depth.',
    lane: 1,
  },
  {
    year: 1894,
    composer: 'Scriabin',
    title: 'Etude Op. 8 No. 12',
    era: 'Modern',
    reason: 'Romantic technique pushed toward chromatic intensity.',
    lane: 5,
  },
  {
    year: 1899,
    composer: 'Scott Joplin',
    title: 'Maple Leaf Rag',
    era: 'Jazz',
    reason: 'Syncopation becomes a written keyboard language.',
    lane: 3,
  },
  {
    year: 1901,
    composer: 'Ravel',
    title: "Jeux d'eau",
    era: 'Modern',
    reason: 'Liquid figuration and orchestral color reshape piano texture.',
    lane: 0,
  },
  {
    year: 1905,
    composer: 'Debussy',
    title: 'Clair de lune',
    era: 'Modern',
    reason: 'Color, resonance, and pedal atmosphere become the subject.',
    lane: 4,
  },
  {
    year: 1910,
    composer: 'Debussy',
    title: 'La cathedrale engloutie',
    era: 'Modern',
    reason: 'Parallel harmony and register build an imagined acoustic space.',
    lane: 2,
  },
  {
    year: 1911,
    composer: 'Bartok',
    title: 'Allegro barbaro',
    era: 'Modern',
    reason: 'Percussive rhythm and folk energy hit the modern piano.',
    lane: 5,
  },
  {
    year: 1911,
    composer: 'Schoenberg',
    title: 'Six Little Piano Pieces, Op. 19',
    era: 'Modern',
    reason: 'Extreme compression and atonality in miniature.',
    lane: 1,
  },
  {
    year: 1912,
    composer: 'Prokofiev',
    title: 'Toccata, Op. 11',
    era: 'Modern',
    reason: 'Motor rhythm and steel-edged virtuosity.',
    lane: 3,
  },
  {
    year: 1921,
    composer: 'Stravinsky',
    title: 'Three Movements from Petrushka',
    era: 'Modern',
    reason: 'Ballet color translated into a massive piano challenge.',
    lane: 0,
  },
  {
    year: 1921,
    composer: 'James P. Johnson',
    title: 'Carolina Shout',
    era: 'Jazz',
    reason: 'Stride piano enters the virtuoso keyboard lineage.',
    lane: 4,
  },
  {
    year: 1929,
    composer: 'Fats Waller',
    title: "Ain't Misbehavin'",
    era: 'Jazz',
    reason: 'Stride charm, harmonic snap, and song-form keyboard style.',
    lane: 2,
  },
  {
    year: 1930,
    composer: 'Copland',
    title: 'Piano Variations',
    era: 'Modern',
    reason: 'Lean modernist architecture and severe sonority.',
    lane: 5,
  },
  {
    year: 1933,
    composer: 'Art Tatum',
    title: 'Tea for Two',
    era: 'Jazz',
    reason: 'Reharmonization and velocity redraw what solo jazz piano can be.',
    lane: 1,
  },
  {
    year: 1944,
    composer: 'Thelonious Monk',
    title: 'Round Midnight',
    era: 'Jazz',
    reason: 'Angular melody, silence, and dissonant voicing become unmistakable style.',
    lane: 3,
  },
  {
    year: 1946,
    composer: 'John Cage',
    title: 'Sonatas and Interludes',
    era: 'Modern',
    reason: 'Prepared piano turns the instrument into a percussion orchestra.',
    lane: 0,
  },
  {
    year: 1951,
    composer: 'Bud Powell',
    title: 'Un Poco Loco',
    era: 'Jazz',
    reason: 'Bebop language crystallizes at the keyboard.',
    lane: 5,
  },
  {
    year: 1953,
    composer: 'Ligeti',
    title: 'Musica ricercata',
    era: 'Modern',
    reason: 'Restriction becomes process, rhythm, and modern drama.',
    lane: 2,
  },
  {
    year: 1958,
    composer: 'Bill Evans',
    title: 'Peace Piece',
    era: 'Jazz',
    reason: 'Modal harmony and repeated ground meet lyrical improvisation.',
    lane: 4,
  },
  {
    year: 1965,
    composer: 'Herbie Hancock',
    title: 'Maiden Voyage',
    era: 'Jazz',
    reason: 'Quartal/modal voicings point toward a modern jazz piano vocabulary.',
    lane: 1,
  },
  {
    year: 1975,
    composer: 'Keith Jarrett',
    title: 'The Koln Concert, Part I',
    era: 'Jazz',
    reason: 'Solo improvisation becomes a large-form concert statement.',
    lane: 5,
  },
  {
    year: 1984,
    composer: 'Chick Corea',
    title: "Children's Songs",
    era: 'Jazz',
    reason: 'Minimal cells, jazz harmony, and classical craft meet.',
    lane: 3,
  },
  {
    year: 1995,
    composer: 'Philip Glass',
    title: 'Etudes, Book 1',
    era: 'Modern',
    reason: 'Minimalist process enters the mainstream piano repertoire.',
    lane: 0,
  },
];

const repertoireDetails: string[] = [
  'Couperin published this rondeau in his Second Book of harpsichord pieces, and its title has been debated ever since. The music is less mysterious than it first looks: overlapping suspensions and style brise lute figuration keep the harmony gently delayed.',
  'Bach wrote the Inventions as teaching pieces, but this first one is also a tiny lesson in composition. One short idea is passed between two voices until counterpoint starts to sound like conversation.',
  "This prelude opens the 1722 Well-Tempered Clavier, Bach's landmark tour through all major and minor keys. Its steady broken chords make harmony itself the main melody.",
  "Scarlatti's K. 141 is famous for rapid repeated notes and hand crossings. Its guitar-like bite and dance energy point toward the Spanish sound world around the composer in Madrid.",
  "C. P. E. Bach's Solfeggietto is a compact 1766 keyboard firework. The single rushing line splits between the hands, turning a scale-study idea into empfindsamer-style drama.",
  "Haydn's C major sonata shows how Classical wit can live inside a small, clear plan. Its balanced phrases and quick changes of character fit the fortepiano age, where touch and timing mattered more than volume.",
  'Mozart titled this finale "Alla Turca," joining an 18th-century European taste for Janissary-inspired color. The repeated notes and bright accents imitate percussion on a keyboard that had none.',
  'Mozart described K. 545 in his own catalog as a sonata for beginners. Its transparency is the challenge: every scale, cadence, and phrase ending is exposed.',
  "Clementi's Op. 36 sonatinas became durable teaching repertoire because they compress sonata form without watering it down. This first one trains clean Classical articulation and clear two-theme contrast.",
  "The Pathetique, composed in the late 1790s, gave Beethoven's piano sonata a public, dramatic voice. The Grave opening sounds almost orchestral before the Allegro turns the keyboard into theater.",
  'Beethoven completed this sonata in 1801, but the "Moonlight" nickname came later from Ludwig Rellstab. Its slow, arpeggiated first movement reverses the usual sonata expectation of a fast opening.',
  'Fur Elise was not published until decades after Beethoven died, and the identity of "Elise" is still debated. Its fame comes from the contrast between a simple, memorable refrain and stormier episodes.',
  'Schubert wrote the Op. 90 impromptus in 1827, near the end of his life. No. 2 turns perpetual motion into song, then interrupts it with a darker middle section.',
  "Mendelssohn's first published Songs Without Words appeared in 1832 under the English title Original Melodies for the Pianoforte. Op. 19 No. 1 shows his central idea: the piano can sing without a singer.",
  'Chopin took the nocturne beyond salon atmosphere and made it a flexible bel canto instrument. In Op. 9 No. 2, each return of the melody is more ornamented, like an aria shaped in real time.',
  "Chopin's first Ballade helped create the instrumental ballade as a piano genre. Its power is narrative: themes return transformed, as if the piano is telling a story without words.",
  'Clara Wieck wrote the Notturno before her marriage to Robert Schumann, when she was already a celebrated touring pianist. The piece blends public virtuoso polish with the private, singing tone of a nocturne.',
  "Traumerei is the seventh piece from Schumann's Kinderszenen, written in 1838. It is not really music for children; it is an adult memory of childhood, distilled into a few tender phrases.",
  "Chopin's Op. 28 Preludes explore all 24 keys in miniature. No. 4 is only a page long, but its descending line and repeated chords create an unusually concentrated grief.",
  "Hensel's September comes from Das Jahr, her 1841 cycle of piano pieces for the months of the year. The cycle was not published complete in her lifetime, making it a major rediscovery of Romantic piano music.",
  "Chopin's Barcarolle is a late work from the mid-1840s, built from the rocking rhythm of a Venetian boat song. Instead of staying decorative, it grows into a broad narrative form with luminous harmony.",
  'Liszt based Liebestraum No. 3 on the idea of a love song transformed for piano. The piece is famous because the melody keeps returning through increasingly difficult textures without losing its vocal shape.',
  "Arietta begins Grieg's long series of Lyric Pieces, first published in 1867. He loved the melody enough to recall it decades later near the close of the final book.",
  'Mussorgsky wrote Pictures at an Exhibition after seeing works by his late friend Viktor Hartmann. The Promenade is the listener walking through the gallery, changing mood from picture to picture.',
  'Satie published the first Gymnopedie in 1888, using a title that evokes ancient dance but remains deliberately elusive. Its plain texture and hovering harmony made it feel modern long before minimalism had a name.',
  'Rachmaninoff wrote this prelude as a 19-year-old, and audiences soon demanded it by shouting "C-sharp." Its bell-like opening earned nicknames such as The Bells of Moscow and helped make it almost too famous for him.',
  "Dreaming comes from Amy Beach's Four Sketches, published in the early 1890s. Beach attached a poetic dream image to the piece, and the music answers with long-breathed melody and subtle chromatic color.",
  'Brahms completed Op. 118 in his late period and dedicated the set to Clara Schumann. This A major Intermezzo sounds intimate, but its inner voices are carefully woven.',
  "Scriabin's Op. 8 No. 12 is an early virtuoso etude from 1894. It still owes something to Chopin, but the harmony and surging left-hand leaps already point toward Scriabin's more ecstatic language.",
  'Maple Leaf Rag, published in 1899, became a model for classic ragtime. Its syncopated right hand over a steady left-hand march helped define a written American keyboard style.',
  "Ravel wrote Jeux d'eau in 1901 and dedicated it to Gabriel Faure. Inspired partly by Liszt's water music, it made shimmering keyboard figuration a new kind of coloristic modernism.",
  "Clair de lune is part of Debussy's Suite bergamasque, begun earlier and revised before publication in 1905. Its moonlit surface is delicate, but the piece depends on precise color, pedal, and harmonic timing.",
  'This Debussy prelude draws on the legend of the drowned city of Ys and its submerged cathedral. Chords rise and fade like bells and chant heard through water.',
  'Bartok wrote Allegro barbaro in 1911 during his experimental piano period. Its hard attacks, ostinatos, and folk-rhythm energy turn the piano into a percussive modern instrument.',
  "Schoenberg's Op. 19 miniatures compress expression into a few gestures rather than developing themes in the old way. Each tiny piece has its own sound world, which was radical in 1911.",
  'Prokofiev composed the Toccata in 1912 and later premiered it himself. The piece updates the old toccata idea with machine-like repeated notes and relentless motor rhythm.',
  'Stravinsky made Three Movements from Petrushka for Arthur Rubinstein in 1921, drawing from his earlier ballet. It asks one pianist to project orchestral color, ballet rhythm, and brutal virtuosity at once.',
  "James P. Johnson's Carolina Shout became a rite of passage for stride pianists. His 1921 recordings helped put jazz piano solo playing on record at a crucial early moment.",
  "Ain't Misbehavin' came from the 1929 Harlem revue Hot Chocolates, with music by Fats Waller and Harry Brooks and lyrics by Andy Razaf. Waller's piano style turns stride into relaxed songbook elegance.",
  "Copland's 1930 Piano Variations was his first major solo piano statement. Its lean, severe language adapts modernist and twelve-tone ideas while still sounding unmistakably like Copland.",
  'Art Tatum made Tea for Two a vehicle for astonishing reharmonization and keyboard speed. His early 1930s solo recordings showed how a popular tune could become a complete virtuoso universe.',
  "Round Midnight became Monk's signature ballad and one of the most recorded jazz standards by a jazz composer. Its beauty depends on crooked intervals, dark harmony, and perfectly placed silence.",
  "Cage's Sonatas and Interludes, written from 1946 to 1948, is his great prepared-piano cycle. Screws, bolts, and other objects alter the strings so the piano becomes a small percussion orchestra.",
  'Bud Powell recorded Un Poco Loco in 1951 with Curly Russell and Max Roach. Its Afro-Cuban rhythmic cell and bebop harmony make the track feel unstable in the most deliberate way.',
  'Ligeti wrote Musica ricercata in the early 1950s, building pieces from severely limited pitch material. The cycle sounds like a composer trying to rebuild musical language from almost nothing.',
  'Bill Evans recorded Peace Piece in 1958 over a repeating two-chord ground. What feels improvised and still became a model for later modal jazz quietness.',
  "Herbie Hancock recorded Maiden Voyage for Blue Note in 1965 after absorbing Miles Davis's modal experiments. The title track uses spacious harmony to suggest an ocean journey rather than a chord-heavy tune.",
  'The Koln Concert was improvised live in Cologne on January 24, 1975. Its unlikely conditions and flowing large-scale form helped make it one of the best-known solo jazz piano recordings.',
  "Chick Corea's Children's Songs are concise piano pieces that join jazz harmony with classical craft. Corea described the project as reaching for simplicity and beauty in the spirit of a child.",
  'Glass wrote the Etudes partly to expand his own piano technique. Book 1 turns minimalist process into studies of touch, voicing, endurance, and gradual change.',
];

const instrumentMilestones: InstrumentMilestone[] = [
  {
    year: 1700,
    title: 'Cristofori pianoforte',
    impact: 'Hammer action creates touch-sensitive soft and loud playing.',
  },
  {
    year: 1821,
    title: 'Double escapement',
    impact: 'Fast repetition feeds Romantic virtuosity.',
  },
  {
    year: 1825,
    title: 'Iron frame',
    impact: 'More tension, more projection, larger halls.',
  },
  {
    year: 1859,
    title: 'Overstrung grand',
    impact: 'Longer bass strings and deeper resonance.',
  },
  {
    year: 1890,
    title: '88-key standard',
    impact: 'A0-C8 becomes the modern concert compass.',
  },
  {
    year: 1939,
    title: 'Novachord',
    impact: 'Electronic keyboard color joins the story.',
  },
  {
    year: 1983,
    title: 'MIDI and DX7',
    impact: 'Digital control and FM tone change keyboard writing.',
  },
];

const sourceLinks: SourceLink[] = [
  {
    label: 'Bachvereniging: WTC I',
    href: 'https://www.bachvereniging.nl/en/bwv/bwv-846',
  },
  {
    label: 'PTNA: Chopin Ballade No. 1',
    href: 'https://enc.piano.or.jp/en/musics/462',
  },
  {
    label: 'PTNA: Chopin Barcarolle',
    href: 'https://enc.piano.or.jp/en/musics/543',
  },
  {
    label: 'Britannica: Moonlight Sonata',
    href: 'https://www.britannica.com/topic/Moonlight-Sonata',
  },
  {
    label: 'Britannica: Suite bergamasque',
    href: 'https://www.britannica.com/topic/Suite-bergamasque',
  },
  {
    label: 'Library of Congress: Maple Leaf Rag',
    href: 'https://lcm.loc.gov/issue/march-april-2025/the-magical-maple-leaf-rag/',
  },
  {
    label: 'John Cage Trust: Sonatas and Interludes',
    href: 'https://johncage.org/pp/John-Cage-Work-Detail.cfm?work_ID=188',
  },
  {
    label: 'Ligeti: Musica ricercata',
    href: 'https://en.gyorgy-ligeti.com/work/musica-ricercata',
  },
  {
    label: 'Herbie Hancock: Maiden Voyage',
    href: 'https://www.herbiehancock.com/music/discography/album/maiden-voyage/',
  },
  {
    label: 'Philip Glass: Etudes',
    href: 'https://philipglass.com/compositions/etudes/',
  },
  {
    label: 'The Met: Cristofori pianofortes',
    href: 'https://www.metmuseum.org/toah/hd/cris/hd_cris.htm',
  },
  {
    label: 'The Met: Babcock iron frame',
    href: 'https://www.metmuseum.org/art/collection/search/504602',
  },
  {
    label: 'Smithsonian: Steinway overstringing',
    href: 'https://americanhistory.si.edu/steinwaydiary/annotations/?id=2043&popup=1',
  },
  {
    label: 'Yamaha: 88-key standard',
    href: 'https://hub.yamaha.com/pianos/p-acoustic/why-arent-there-more-than-88-keys-on-a-piano/',
  },
];

const yearMarkers = [1720, 1760, 1800, 1840, 1880, 1920, 1960, 2000];

const yearToX = (year: number) => {
  const boundedYear = Math.min(Math.max(year, TIMELINE_START), TIMELINE_END);
  const usableWidth = ROADMAP_WIDTH - ROADMAP_INSET * 2;
  return ROADMAP_INSET + ((boundedYear - TIMELINE_START) / TIMELINE_SPAN) * usableWidth;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const nodeToPoint = (node: RepertoireNode, x = yearToX(node.year)): RoadmapPoint => ({
  x,
  y: PATH_MIDLINE + PATH_LANE_OFFSETS[node.lane],
});

const getRoadmapPoints = (nodes: RepertoireNode[]) => {
  const yearTotals = nodes.reduce<Record<number, number>>((totals, node) => {
    totals[node.year] = (totals[node.year] || 0) + 1;
    return totals;
  }, {});
  const yearIndexes: Record<number, number> = {};
  let previousX = ROADMAP_INSET - MIN_NODE_X_GAP;

  return nodes.map((node) => {
    const yearTotal = yearTotals[node.year] || 1;
    const yearIndex = yearIndexes[node.year] || 0;
    yearIndexes[node.year] = yearIndex + 1;

    const duplicateOffset =
      yearTotal > 1 ? (yearIndex - (yearTotal - 1) / 2) * DUPLICATE_YEAR_X_SPACING : 0;
    const minimumX = Math.min(previousX + MIN_NODE_X_GAP, ROADMAP_WIDTH - ROADMAP_INSET);
    const x = clamp(yearToX(node.year) + duplicateOffset, minimumX, ROADMAP_WIDTH - ROADMAP_INSET);
    previousX = x;

    return nodeToPoint(node, x);
  });
};

const createPathD = (points: RoadmapPoint[]) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const segmentSlopes = points.slice(0, -1).map((point, index) => {
    const nextPoint = points[index + 1];
    const dx = Math.max(nextPoint.x - point.x, 1);
    return (nextPoint.y - point.y) / dx;
  });

  const tangents = points.map((_point, index) => {
    if (index === 0) return segmentSlopes[0] * PATH_CURVE_TENSION;
    if (index === points.length - 1) {
      return segmentSlopes[segmentSlopes.length - 1] * PATH_CURVE_TENSION;
    }

    const previousSlope = segmentSlopes[index - 1];
    const nextSlope = segmentSlopes[index];
    if (previousSlope === 0 || nextSlope === 0 || previousSlope * nextSlope < 0) {
      return 0;
    }

    const previousDx = Math.max(points[index].x - points[index - 1].x, 1);
    const nextDx = Math.max(points[index + 1].x - points[index].x, 1);
    return (
      ((previousSlope * nextDx + nextSlope * previousDx) / (previousDx + nextDx)) *
      PATH_CURVE_TENSION
    );
  });

  return points.slice(1).reduce((path, point, index) => {
    const previousPoint = points[index];
    const dx = Math.max(point.x - previousPoint.x, 1);
    const controlXOffset = dx / 3;
    const minY = Math.min(previousPoint.y, point.y);
    const maxY = Math.max(previousPoint.y, point.y);
    const firstControlY = clamp(previousPoint.y + tangents[index] * controlXOffset, minY, maxY);
    const secondControlY = clamp(point.y - tangents[index + 1] * controlXOffset, minY, maxY);

    return `${path} C ${previousPoint.x + controlXOffset} ${firstControlY}, ${
      point.x - controlXOffset
    } ${secondControlY}, ${point.x} ${point.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
};

const getEraColor = (eraName: EraName) =>
  eras.find((era) => era.name === eraName)?.color || '#77c9b5';

const HistoryOfPianoMusic = () => {
  const location = useLocation();
  const [showInfo, setShowInfo] = useState(false);
  const [isRoadmapDragging, setIsRoadmapDragging] = useState(false);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState<number | null>(null);
  // Android's Back closes the piece details as their X does.
  // Drawn at z-index 60 (.history-piece-panel).
  useHardwareBackClosesLayer(selectedNodeIndex !== null, () => setSelectedNodeIndex(null), 60);
  const roadmapScrollRef = useRef<HTMLDivElement | null>(null);
  const roadmapDragRef = useRef<RoadmapDragState>({
    active: false,
    pointerId: null,
    startX: 0,
    scrollLeft: 0,
  });

  const roadmapPoints = useMemo(() => getRoadmapPoints(repertoirePath), []);
  const roadmapPathD = useMemo(() => createPathD(roadmapPoints), [roadmapPoints]);
  const selectedNode = selectedNodeIndex !== null ? repertoirePath[selectedNodeIndex] : null;
  const selectedNodeDetail =
    selectedNodeIndex !== null ? repertoireDetails[selectedNodeIndex] : undefined;

  const endRoadmapDrag = useCallback((event?: ReactPointerEvent<HTMLDivElement>) => {
    const scrollElement = roadmapScrollRef.current;
    if (event && scrollElement?.hasPointerCapture(event.pointerId)) {
      scrollElement.releasePointerCapture(event.pointerId);
    }
    roadmapDragRef.current.active = false;
    roadmapDragRef.current.pointerId = null;
    setIsRoadmapDragging(false);
  }, []);

  const handleRoadmapPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const scrollElement = roadmapScrollRef.current;
    if (!scrollElement) return;

    roadmapDragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: scrollElement.scrollLeft,
    };
    scrollElement.setPointerCapture(event.pointerId);
    setIsRoadmapDragging(true);
  }, []);

  const handleRoadmapPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = roadmapDragRef.current;
    const scrollElement = roadmapScrollRef.current;
    if (!dragState.active || dragState.pointerId !== event.pointerId || !scrollElement) return;

    event.preventDefault();
    scrollElement.scrollLeft = dragState.scrollLeft - (event.clientX - dragState.startX);
  }, []);

  const handleRoadmapPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (roadmapDragRef.current.pointerId !== event.pointerId) return;
      endRoadmapDrag(event);
    },
    [endRoadmapDrag],
  );

  const handleRoadmapWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    const scrollElement = event.currentTarget;
    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
    if (maxScrollLeft <= 0) return;

    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const scrollDelta = event.deltaX;
    if (scrollDelta === 0) return;

    const nextScrollLeft = clamp(scrollElement.scrollLeft + scrollDelta, 0, maxScrollLeft);
    if (nextScrollLeft === scrollElement.scrollLeft) return;

    event.preventDefault();
    scrollElement.scrollLeft = nextScrollLeft;
  }, []);

  useEffect(() => {
    awsRum?.recordPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (selectedNodeIndex === null) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedNodeIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeIndex]);

  return (
    <div className="history-page">
      <Menu selected="History" />
      <main className="history-main">
        <section className="history-hero">
          <div className="history-hero-copy">
            <div className="history-title-row">
              <h1>History of Piano Music</h1>
              <button
                className="history-info-button"
                type="button"
                aria-label="About this roadmap"
                aria-expanded={showInfo}
                aria-controls="history-info-panel"
                onClick={() => setShowInfo((current) => !current)}
              >
                i
              </button>
            </div>
            {showInfo && (
              <p className="history-intro" id="history-info-panel">
                A chronological path through the keyboard pieces that changed piano writing, from
                Bach and Scarlatti to Chopin, Debussy, Monk, Hancock, and Glass.
              </p>
            )}
          </div>
        </section>

        <section className="history-roadmap-shell" aria-label="Piano repertoire roadmap">
          <div
            className={`history-roadmap-scroll ${isRoadmapDragging ? 'is-dragging' : ''}`}
            ref={roadmapScrollRef}
            onPointerDown={handleRoadmapPointerDown}
            onPointerMove={handleRoadmapPointerMove}
            onPointerUp={handleRoadmapPointerUp}
            onPointerCancel={handleRoadmapPointerUp}
            onLostPointerCapture={endRoadmapDrag}
            onWheel={handleRoadmapWheel}
          >
            <div
              className="history-roadmap-stage"
              style={{
                width: `${ROADMAP_WIDTH}px`,
                height: `${ROADMAP_HEIGHT}px`,
              }}
            >
              <div className="history-era-backdrop" aria-hidden="true">
                {eras.map((era) => (
                  <div
                    className="history-era-window"
                    key={era.name}
                    style={{
                      left: `${yearToX(era.start)}px`,
                      width: `${Math.max(yearToX(era.end) - yearToX(era.start), 50)}px`,
                      borderColor: era.color,
                    }}
                  >
                    <span style={{ color: era.color }}>{era.name}</span>
                    <p>{era.summary}</p>
                  </div>
                ))}
              </div>

              <div className="history-year-axis" aria-hidden="true">
                {yearMarkers.map((year) => (
                  <div
                    className="history-year-marker"
                    key={year}
                    style={{ left: `${yearToX(year)}px` }}
                  >
                    <span>{year}</span>
                  </div>
                ))}
              </div>

              <svg
                className="history-path-svg"
                width={ROADMAP_WIDTH}
                height={ROADMAP_HEIGHT}
                viewBox={`0 0 ${ROADMAP_WIDTH} ${ROADMAP_HEIGHT}`}
                aria-hidden="true"
              >
                <path className="history-path-shadow" d={roadmapPathD} />
                <path className="history-path-line" d={roadmapPathD} />
              </svg>

              {instrumentMilestones.map((milestone) => (
                <article
                  className="history-instrument-flag"
                  key={`${milestone.year}-${milestone.title}`}
                  style={{ left: `${yearToX(milestone.year)}px` }}
                >
                  <div className="history-instrument-pin" />
                  <span>{milestone.year}</span>
                  <strong>{milestone.title}</strong>
                  <p>{milestone.impact}</p>
                </article>
              ))}

              {repertoirePath.map((node, index) => {
                const point = roadmapPoints[index];
                const isUpper = point.y < PATH_MIDLINE;
                let labelClass = isUpper ? 'label-above' : 'label-below';
                if (point.y < 250) {
                  labelClass = 'label-below';
                } else if (point.y > 660) {
                  labelClass = 'label-above';
                }
                const eraColor = getEraColor(node.era);

                return (
                  <article
                    className={`history-path-node ${isUpper ? 'is-upper' : 'is-lower'} ${
                      node.spotlight ? 'is-spotlight' : ''
                    } ${selectedNodeIndex === index ? 'is-selected' : ''}`}
                    key={`${node.year}-${node.composer}-${node.title}`}
                    aria-label={`${node.year}: ${node.title} by ${node.composer}. ${node.reason}`}
                    style={{
                      left: `${point.x}px`,
                      top: `${point.y}px`,
                    }}
                  >
                    <button
                      className="history-node-orb"
                      type="button"
                      aria-label={`Read about ${node.title} by ${node.composer}`}
                      aria-haspopup="dialog"
                      aria-expanded={selectedNodeIndex === index}
                      aria-controls="history-piece-panel"
                      style={{ backgroundColor: eraColor, borderColor: eraColor }}
                      onClick={() => setSelectedNodeIndex(index)}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <span>{index + 1}</span>
                    </button>
                    <div className={`history-node-label ${labelClass}`}>
                      <span className="history-node-year">{node.year}</span>
                      <h3>{node.title}</h3>
                      <p className="history-node-composer">{node.composer}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {selectedNode && selectedNodeDetail && (
          <aside
            className="history-piece-panel"
            id="history-piece-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="history-piece-title"
          >
            <div className="history-piece-panel-topline">
              <span style={{ color: getEraColor(selectedNode.era) }}>
                {selectedNode.year} / {selectedNode.era}
              </span>
              <button
                className="history-piece-close"
                type="button"
                aria-label="Close piece details"
                onClick={() => setSelectedNodeIndex(null)}
              >
                <X aria-hidden="true" size={17} strokeWidth={2.4} />
              </button>
            </div>
            <h2 id="history-piece-title">{selectedNode.title}</h2>
            <p className="history-piece-composer">{selectedNode.composer}</p>
            <p className="history-piece-detail">{selectedNodeDetail}</p>
            <div className="history-piece-actions">
              <button className="history-piece-play" type="button" disabled>
                <Play aria-hidden="true" size={15} fill="currentColor" strokeWidth={2.3} />
                <span>Play repertoire</span>
              </button>
              <span>Repertoire upload pending</span>
            </div>
          </aside>
        )}

        <section className="history-composer-map" aria-label="Composer and piece map">
          <div className="history-section-heading">
            <p className="history-kicker">Composer map</p>
            <h2>What the path covers</h2>
          </div>
          <div className="history-composer-grid">
            {eras.map((era) => {
              const nodes = repertoirePath.filter((node) => node.era === era.name);
              return (
                <article key={era.name}>
                  <div className="history-era-title">
                    <span style={{ backgroundColor: era.color }} />
                    <h3>{era.name}</h3>
                  </div>
                  <p>{era.summary}</p>
                  <ul>
                    {nodes.slice(0, 7).map((node) => (
                      <li key={`${era.name}-${node.composer}-${node.title}`}>
                        <strong>{node.composer}</strong>
                        <span>{node.title}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <footer className="history-sources">
          <span>Reference notes:</span>
          {sourceLinks.map((source) => (
            <a key={source.href} href={source.href} target="_blank" rel="noreferrer">
              {source.label}
            </a>
          ))}
        </footer>
      </main>
    </div>
  );
};

export default HistoryOfPianoMusic;
